import { useEffect } from "react";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/link";
import { StatusNote } from "@/components/status-note";

// Was src/app/error.tsx — wired as the router's defaultErrorComponent, so it
// catches anything thrown while rendering a route, including Convex query
// errors (e.g. an admin-gated query rejecting after access is revoked).
//
// The raw message is still never shown: a client error can carry internals.
// Next's `error.digest` is gone with the Next server — there is no server log
// to correlate against any more, so there is no reference to print.
export function RouteError({ error, reset }: ErrorComponentProps) {
  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <StatusNote>
      <div className="flex flex-col gap-3">
        <h1 className="text-lg font-semibold tracking-tight">
          Something went wrong
        </h1>
        <p className="text-muted-foreground text-sm leading-6">
          We couldn&apos;t load this page. Try again, or head back to the
          dashboard. If it keeps happening, let an admin know.
        </p>
        <div className="mt-2 flex items-center justify-center gap-2">
          <Button variant="outline" onClick={() => reset()}>
            Try again
          </Button>
          <Button nativeButton={false} render={<Link href="/" />}>
            Go to dashboard
          </Button>
        </div>
      </div>
    </StatusNote>
  );
}
